import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Check, X, ChevronRight, Rocket } from "lucide-react";

/**
 * 4-step activation card shown on the shop dashboard right after approval.
 * Auto-hides once all steps are complete OR the shop dismisses it.
 */
export function ShopOnboardingChecklist() {
  const [data, setData] = useState(null);
  const [dismissing, setDismissing] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/shop/onboarding");
        setData(res.data);
        // One-time celebration: shop just hit 4/4 and hasn't been celebrated yet.
        if (res.data.all_done && !res.data.celebrated && !res.data.dismissed) {
          confetti({
            particleCount: 120,
            spread: 80,
            origin: { y: 0.25 },
            colors: ["#FF3B30", "#FF9500", "#34C759", "#007AFF", "#FFFFFF"],
          });
          toast.success("You're ready to win jobs 🚀", {
            description: "Onboarding complete. Every new job in your radius will land in your dashboard.",
            duration: 8000,
          });
          api.post("/shop/onboarding/celebrated").catch(() => {});
        }
      } catch {
        // Silently ignore — shop may be unapproved or unauthorized.
      }
    })();
  }, []);

  if (!data || hidden) return null;
  if (data.dismissed) return null;
  if (data.all_done) return null;

  const onDismiss = async () => {
    setDismissing(true);
    try {
      await api.post("/shop/onboarding/dismiss");
      setHidden(true);
    } finally {
      setDismissing(false);
    }
  };

  const pct = Math.round((data.completed / data.total) * 100);

  return (
    <div
      className="border border-white/10 bg-gradient-to-br from-[#121214] to-[#0c0c0e] p-5 sm:p-6 mb-6"
      data-testid="shop-onboarding-checklist"
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 bg-[#FF3B30]/10 border border-[#FF3B30]/30 flex items-center justify-center shrink-0">
            <Rocket className="w-4 h-4 text-[#FF3B30]" strokeWidth={2.2} />
          </div>
          <div>
            <div className="label-eyebrow text-[#FF3B30] mb-1">GET ACTIVATED</div>
            <h3 className="font-heading text-xl font-bold tracking-tight">Finish setup to start winning jobs</h3>
            <p className="text-zinc-500 text-xs mt-1">{data.completed} of {data.total} complete · ~3 minutes</p>
          </div>
        </div>
        <button
          onClick={onDismiss}
          disabled={dismissing}
          className="text-zinc-600 hover:text-zinc-300 p-1 transition-colors"
          title="Dismiss"
          data-testid="onboarding-dismiss-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-white/5 mb-5 overflow-hidden">
        <div
          className="h-full bg-[#FF3B30] transition-all duration-500"
          style={{ width: `${pct}%` }}
          data-testid="onboarding-progress-bar"
        />
      </div>

      <ul className="space-y-1">
        {data.steps.map((step, idx) => (
          <li key={step.key}>
            <Link
              to={step.action_url}
              className={`group flex items-center gap-3 px-2 py-2.5 transition-colors ${step.done ? "opacity-60" : "hover:bg-white/5"}`}
              data-testid={`onboarding-step-${step.key}`}
            >
              <div className={`w-5 h-5 flex items-center justify-center shrink-0 border ${step.done ? "bg-[#34C759]/20 border-[#34C759]/40" : "border-white/15"}`}>
                {step.done ? (
                  <Check className="w-3 h-3 text-[#34C759]" strokeWidth={3} />
                ) : (
                  <span className="text-[10px] font-mono text-zinc-500">{idx + 1}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-medium ${step.done ? "line-through text-zinc-500" : "text-white"}`}>{step.label}</div>
                {!step.done && <div className="text-xs text-zinc-500 mt-0.5 truncate">{step.description}</div>}
              </div>
              {!step.done && (
                <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-[#FF3B30] transition-colors shrink-0" />
              )}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
